import React, { useEffect, useState } from 'react';
import { Drawer, Form, Input, Button, message, Modal, Tree } from 'antd';
import { MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ADD_PERMISSION_PACKAGE } from '../graphql/mutations/createPermissionPackage';
import { UPDATE_PERMISSION_PACKAGE } from '../graphql/mutations/updatePermissionPackage';
import { DELETE_PERMISSION_PACKAGE } from '../graphql/mutations/deletePermissionPackage';
import type { PermissionPackageApiRequestInput } from '../graphql/mutations/createPermissionPackage';
import {
  LIST_ALL_RESOURCES,
  type ListAllResourcesResponse,
  type ResourceType as ResourceOptionType,
} from '../../resource/graphql/queries/listAllResources';
import type { PermissionPackageDetailData } from '../graphql/queries/getPermissionPackageDetailById';
import { getPlatformPermissionErrorMessage } from '../../utils/errorCode';
import { HasResource } from '../../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

interface PermissionPackageDrawerProps {
  visible: boolean;
  onClose: () => void;
  /** 新建为 null；编辑/详情为详情接口返回的数据（含 resources） */
  editRecord: PermissionPackageDetailData | null;
  onSuccess: () => void;
}

const PermissionPackageDrawer: React.FC<PermissionPackageDrawerProps> = ({
  visible,
  onClose,
  editRecord,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const selectedResourceCodes = Form.useWatch('resourceCodes', form) as string[] | undefined;
  const isEdit = !!editRecord;
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  const { data: resourceListData, loading: resourceListLoading } = useQuery<ListAllResourcesResponse>(
    LIST_ALL_RESOURCES,
    {
      skip: !visible,
      fetchPolicy: 'network-only',
    }
  );

  const resourceOptions = React.useMemo(
    () => resourceListData?.listAllResources ?? [],
    [resourceListData]
  );

  const resourceTreeData = React.useMemo(() => {
    const byId = new Map<number, { key: string; title: string; children: any[]; parentId: number }>();
    resourceOptions.forEach((r: ResourceOptionType) => {
      byId.set(Number(r.id), {
        key: r.code,
        title: r.name || r.code,
        children: [],
        parentId: r.parentId != null ? Number(r.parentId) : 0,
      });
    });

    const roots: Array<{ key: string; title: string; children: any[] }> = [];
    byId.forEach((node) => {
      const parent = node.parentId > 0 ? byId.get(node.parentId) : undefined;
      if (parent) parent.children.push(node);
      else roots.push(node);
    });
    return roots;
  }, [resourceOptions]);

  const parentCodeByCode = React.useMemo(() => {
    const codeById = new Map<string, string>();
    resourceOptions.forEach((r) => {
      codeById.set(String(r.id), String(r.code));
    });
    const map = new Map<string, string>();
    resourceOptions.forEach((r) => {
      const selfCode = String(r.code);
      const pid = r.parentId != null ? String(r.parentId) : '0';
      const parentCode = codeById.get(pid);
      if (parentCode) {
        map.set(selfCode, parentCode);
      }
    });
    return map;
  }, [resourceOptions]);

  const childrenCodeByCode = React.useMemo(() => {
    const map = new Map<string, string[]>();
    resourceTreeData.forEach((root) => {
      const walk = (node: { key: string; children?: any[] }) => {
        const self = String(node.key);
        const children = (node.children ?? []).map((child) => String(child.key));
        map.set(self, children);
        (node.children ?? []).forEach((child) => walk(child));
      };
      walk(root);
    });
    return map;
  }, [resourceTreeData]);

  const autoExpandedKeysForSelected = React.useMemo(() => {
    const selected = new Set((selectedResourceCodes ?? []).map((k) => String(k)));
    const keys = new Set<string>();
    selected.forEach((code) => {
      let parent = parentCodeByCode.get(code);
      const seen = new Set<string>();
      while (parent && !seen.has(parent)) {
        seen.add(parent);
        keys.add(parent);
        parent = parentCodeByCode.get(parent);
      }
      if ((childrenCodeByCode.get(code) ?? []).length > 0) {
        keys.add(code);
      }
    });
    return Array.from(keys);
  }, [childrenCodeByCode, parentCodeByCode, selectedResourceCodes]);

  const existingResourceCodes = React.useMemo(
    () => new Set(resourceOptions.map((r) => String(r.code))),
    [resourceOptions]
  );

  // 过滤掉资源树中已不存在的码，避免旧码在 checkedKeys 中幽灵显示
  const validCheckedKeys = React.useMemo(
    () =>
      (selectedResourceCodes ?? []).filter((c) =>
        existingResourceCodes.size === 0 ? true : existingResourceCodes.has(String(c))
      ),
    [selectedResourceCodes, existingResourceCodes]
  );

  const normalizeWithAncestors = React.useCallback(
    (codes: string[]): string[] => {
      const set = new Set(codes.map((c) => String(c)));
      const currentCodes = Array.from(set);
      currentCodes.forEach((code) => {
        const seen = new Set<string>();
        let parent = parentCodeByCode.get(code);
        while (parent && !seen.has(parent)) {
          seen.add(parent);
          set.add(parent);
          parent = parentCodeByCode.get(parent);
        }
      });
      return Array.from(set);
    },
    [parentCodeByCode]
  );

  const [addPermissionPackage, { loading: createLoading }] = useMutation(ADD_PERMISSION_PACKAGE);
  const [updatePermissionPackage, { loading: updateLoading }] = useMutation(UPDATE_PERMISSION_PACKAGE);
  const [deletePermissionPackage, { loading: deleteLoading }] = useMutation(DELETE_PERMISSION_PACKAGE);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setDeleteModalOpen(false);
      setExpandedKeys([]);
      return;
    }

    // 资源树未加载完成时不初始化；
    // loading=false 但 data 仍为 undefined 说明 query 尚未发出（skip 刚切换），也需等待
    if (resourceListLoading || !resourceListData) return;

    form.resetFields();

    if (editRecord) {
      // 初始化只做交集，不自动补祖先；祖先的自动选中是用户勾选时的操作便利，不是初始状态
      const filtered = (editRecord.resources ?? []).filter((c) => existingResourceCodes.has(String(c)));
      form.setFieldsValue({
        id: editRecord.id,
        name: editRecord.name,
        description: editRecord.description ?? '',
        resourceCodes: filtered,
      });
    } else {
      form.setFieldsValue({ resourceCodes: [] });
    }
  }, [visible, editRecord, form, resourceListLoading, resourceListData, existingResourceCodes]);

  useEffect(() => {
    if (!visible) return;
    setExpandedKeys(autoExpandedKeysForSelected);
  }, [visible, isEdit, autoExpandedKeysForSelected]);

  const handleClose = () => {
    form.resetFields();
    setDeleteModalOpen(false);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const codes = (values.resourceCodes as string[]) ?? [];

      if (isEdit && editRecord) {
        const input: PermissionPackageApiRequestInput = {
          id: Number(editRecord.id),
          name: String(values.name).trim(),
          description: String(values.description ?? '').trim(),
          resourceCodes: codes,
        };
        const res = await updatePermissionPackage({ variables: { input } });
        const result = res.data?.updatePermissionPackage;
        if (result?.success) {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.success(toastText);
          form.resetFields();
          onSuccess();
          onClose();
        } else {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.error(toastText);
        }
      } else {
        const input: PermissionPackageApiRequestInput = {
          name: String(values.name).trim(),
          description: String(values.description ?? '').trim(),
          resourceCodes: codes,
        };
        const res = await addPermissionPackage({ variables: { input } });
        const result = res.data?.addPermissionPackage;
        if (result?.success) {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.success(toastText);
          form.resetFields();
          onSuccess();
          onClose();
        } else {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.error(toastText);
        }
      }
    } catch (e: any) {
      if (Array.isArray(e?.errorFields)) return;
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    }
  };

  const handleDelete = () => {
    if (!isEdit || !editRecord?.id) return;
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!isEdit || !editRecord?.id) return;
    try {
      const res = await deletePermissionPackage({ variables: { input: { id: Number(editRecord.id) } } });
      const result = res.data?.deletePermissionPackage;
      if (result?.success) {
        const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
        if (toastText) message.success(toastText);
        setDeleteModalOpen(false);
        onSuccess();
        onClose();
      } else {
        const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
        if (toastText) message.error(toastText);
      }
    } catch (e: any) {
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    }
  };

  const loading = createLoading || updateLoading;

  return (
    <Drawer
      title={isEdit ? t('central.platformPermission.permissionPackage.drawer.edit') : t('central.platformPermission.permissionPackage.drawer.add')}
      open={visible}
      onClose={handleClose}
      destroyOnClose
      width={520}
      footer={
        <div className="flex justify-between gap-2">
          <div>
            {isEdit && (
                <HasResource resourceCodes={[ResourceCode.permissionPackage.DELETE]}>
                  <Button
                      loading={deleteLoading}
                      onClick={handleDelete}
                      className="bg-red-500 border-red-500 text-white hover:!bg-red-500 hover:!border-red-500 hover:!text-white"
                  >
                    {t('components.button.delete')}
                  </Button>
                </HasResource>
            )}
          </div>
          <div className="flex gap-2">
            <HasResource resourceCodes={[ResourceCode.permissionPackage.ADD, ResourceCode.permissionPackage.UPDATE]}>
              <Button
                  type="primary"
                  loading={loading}
                  onClick={handleSubmit}
              >
                {t('components.button.confirm')}
              </Button>
            </HasResource>
            <Button onClick={onClose}>
              {t('components.button.cancel')}
            </Button>
          </div>
        </div>
      }
    >
      <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} labelAlign="left" colon>
        {isEdit && (
          <Form.Item label={t('central.platformPermission.permissionPackage.fields.id')} name="id">
            <Input disabled />
          </Form.Item>
        )}
        <Form.Item
          name="name"
          label={t('central.platformPermission.permissionPackage.fields.name')}
          rules={[{ required: true, message: t('central.platformPermission.permissionPackage.validation.nameRequired') as string }]}
        >
          <Input maxLength={100} placeholder={t('components.input.typeIn') as string} />
        </Form.Item>
        <Form.Item
          name="description"
          label={t('central.platformPermission.permissionPackage.fields.description')}
        >
          <Input.TextArea rows={2} maxLength={500} placeholder={t('components.input.typeIn') as string} />
        </Form.Item>
        <Form.Item
          name="resourceCodes"
          label={t('central.platformPermission.permissionPackage.fields.resources')}
          required
          rules={[
            {
              validator: async (_, value: string[] | null | undefined) => {
                if (Array.isArray(value) && value.length > 0) return;
                throw new Error(t('central.platformPermission.errorCodes.PACKAGE_RESOURCE_CODES_REQUIRED') as string);
              },
            },
          ]}
        >
          <Tree
            checkable
            checkStrictly
            selectable={false}
            disabled={resourceListLoading}
            treeData={resourceTreeData}
            checkedKeys={validCheckedKeys}
            expandedKeys={expandedKeys}
            onExpand={(keys) => {
              const next = (keys as React.Key[]).map((k) => String(k));
              const prev = expandedKeys.map((k) => String(k));
              const prevSet = new Set(prev);
              const nextSet = new Set(next);

              const collapsedNow = prev.filter((k) => !nextSet.has(k));
              if (collapsedNow.length === 0) {
                setExpandedKeys(next);
                return;
              }

              const toRemove = new Set<string>();
              const collectDescendants = (code: string) => {
                const stack = [...(childrenCodeByCode.get(code) ?? [])];
                while (stack.length > 0) {
                  const child = String(stack.pop()!);
                  if (toRemove.has(child)) continue;
                  toRemove.add(child);
                  (childrenCodeByCode.get(child) ?? []).forEach((nextChild) => stack.push(nextChild));
                }
              };

              collapsedNow.forEach((code) => {
                toRemove.add(String(code));
                collectDescendants(String(code));
              });

              const cleaned = Array.from(nextSet).filter((k) => !toRemove.has(k));
              // 保持已存在的展开项顺序更稳定
              const ordered = prev.filter((k) => cleaned.includes(k)).concat(cleaned.filter((k) => !prevSet.has(k)));
              setExpandedKeys(ordered);
            }}
            switcherIcon={(props: any) => {
              if (props?.isLeaf) return null;
              const expanded = Boolean(props?.expanded);
              return (
                <span
                  className="inline-flex items-center justify-center align-middle"
                  style={{ lineHeight: 0, position: 'relative', top: -1 }}
                >
                  {expanded ? (
                    <MinusSquareOutlined className="text-gray-500" />
                  ) : (
                    <PlusSquareOutlined className="text-gray-500" />
                  )}
                </span>
              );
            }}
            onCheck={(_, info: any) => {
              const current = new Set((selectedResourceCodes ?? []).map((k) => String(k)));
              const nodeCode = String(info?.node?.key ?? '');
              if (!nodeCode) return;

              if (info?.checked) {
                current.add(nodeCode);
                let parent = parentCodeByCode.get(nodeCode);
                const seen = new Set<string>();
                while (parent && !seen.has(parent)) {
                  seen.add(parent);
                  current.add(parent);
                  parent = parentCodeByCode.get(parent);
                }
              } else {
                const toRemove = new Set<string>([nodeCode]);
                const stack = [...(childrenCodeByCode.get(nodeCode) ?? [])];
                while (stack.length > 0) {
                  const child = stack.pop()!;
                  if (toRemove.has(child)) continue;
                  toRemove.add(child);
                  (childrenCodeByCode.get(child) ?? []).forEach((next) => stack.push(next));
                }
                toRemove.forEach((code) => current.delete(code));
              }

              form.setFieldsValue({ resourceCodes: Array.from(current) });
            }}
          />
        </Form.Item>
      </Form>
      <Modal
        title={t('central.platformPermission.permissionPackage.deleteConfirmTitle') as string}
        open={deleteModalOpen}
        onOk={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
        okText={t('components.button.confirm') as string}
        cancelText={t('components.button.cancel') as string}
        okButtonProps={{
          loading: deleteLoading,
          className: 'bg-purple-600 border-purple-600 hover:!bg-purple-700 hover:!border-purple-700',
        }}
      >
        <p>{t('central.platformPermission.permissionPackage.deleteConfirmContent') as string}</p>
      </Modal>
    </Drawer>
  );
};

export default PermissionPackageDrawer;
